export interface TableRunSuccess {
  msg: string;
  "no-results"?: boolean;
  "write-table-rows"?: {
    stats: {
      insert?: number;
      update?: number;
      delete?: number;
    };
  };
}

export interface TableRunError {
  error: "true";
  error_line: string;
  message: string;
  stack_trace: string;
  error_line_number: string;
}

export type TableRun = TableRunSuccess | TableRunError;

export interface TableRunResponses {
  [tableId: string]: TableRunSuccess;
}

export enum TriggerIndex {
  ALWAYS = 0,
  CREATE = 1,
  UPDATE = 2,
  DELETE = 3,
}

export enum TableTrigger {
  ALWAYS = "always",
  CREATE = "create",
  UPDATE = "update",
  DELETE = "delete",
}

export const triggerToIndex = {
  [TableTrigger.ALWAYS]: TriggerIndex.ALWAYS,
  [TableTrigger.CREATE]: TriggerIndex.CREATE,
  [TableTrigger.UPDATE]: TriggerIndex.UPDATE,
  [TableTrigger.DELETE]: TriggerIndex.DELETE,
};

export const indexToTrigger = {
  [TriggerIndex.ALWAYS]: TableTrigger.ALWAYS,
  [TriggerIndex.CREATE]: TableTrigger.CREATE,
  [TriggerIndex.UPDATE]: TableTrigger.UPDATE,
  [TriggerIndex.DELETE]: TableTrigger.DELETE,
};

export type CodeType = "data-transform" | "side-effect";

export type TableSpecOutput = { columns: TableColumn[] };

export interface TableColumn {
  name: string;
  is_primary_key?: boolean;
}

export interface TableSpec {
  code: string;
  name: string;
  "code-type": CodeType;
  description: "descrption";
  inputs: string[];
  output: TableSpecOutput;
  "table-id": string;
  "execute-error"?: TableRunError;
  trigger?: TableTrigger;
  update_interval: TableRunFrequency;
}

export interface TableMap {
  [id: string]: Table;
}

export interface TableSpecMap {
  [id: string]: TableSpec;
}

export interface Table {
  name: string;
  "file-id": string;
  "primary-key": string[];
  location: string;
  header: string[];
  "table-id": string;
  rows?: string[][];
}

export interface TableNotFound {
  "not-found": true;
  "table-id"?: string;
  "file-id"?: string;
}

export interface TableNotFoundMap {
  [k: string]: TableNotFound;
}

export type TableOrNotFound = Table | TableNotFound;

export const tableOrNotFoundAsTable = (tableOrNotFound: TableOrNotFound) => {
  return "name" in tableOrNotFound ? (tableOrNotFound as Table) : undefined;
};

export const tableOrNotFoundAsTableNotFound = (
  tableOrNotFound: TableOrNotFound
) => {
  return "not-found" in tableOrNotFound
    ? (tableOrNotFound as TableNotFound)
    : undefined;
};

export interface TableLogs {
  logs: string;
  "app-id": string;
  "table-id": string;
}

export interface TablePagesMap {
  [id: string]: { [index: number]: string[][] };
}

export interface TableLogsMap {
  [tableId: string]: string;
}

export enum TableType {
  "CODE_TABLE" = "CODE_TABLE",
  "DATA_TABLE" = "DATA_TABLE",
  "LOADING" = "LOADING",
}

export interface GetTableRunHistoryResponse {
  "table-id": string;
  "app-id": string;
  "table-runs": TableRunHistoryElement[];
}

export const tableRunAsSuccess = (tableRun: TableRun) => {
  return "no-results" in tableRun || "write-table-rows" in tableRun
    ? (tableRun as TableRunSuccess)
    : undefined;
};

export const tableRunAsError = (tableRun: TableRun) => {
  return "error" in tableRun ? (tableRun as TableRunError) : undefined;
};

export interface TableRunHistoryElement {
  "run-id": string;
  timestamp: string;
  "table-run": TableRun;
}

export interface TableRunHistoryMap {
  [k: string]: GetTableRunHistoryResponse;
}

export type RunControl = "resumed" | "paused";

export interface GetTableRunControlResponse {
  "table-id": string;
  "app-id": string;
  "run-control": RunControl;
}

export interface TableRunControlMap {
  [k: string]: RunControl;
}

export interface RunControlSetting {
  tableId: string;
  runControl: RunControl;
}

export interface TableRunFrequency {
  minutes?: number;
  seconds?: number;
  hours?: number;
  days?: number;
}

export interface TableLoadingMap {
  [tableId: string]: boolean;
}

export interface CreateTableRequest {
  appId: string;
  pageId: string;
  tableName: string;
  codeType: CodeType;
}

export interface UpdateTableColumnsRequest {
  appId: string;
  tableId: string;
  columns: TableColumn[];
}

export interface UpdateTableInputsRequest {
  appId: string;
  tableId: string;
  inputs: string[];
}

export interface FullTableMap {
  [tableId: string]: TableFile;
}

export interface TableFile {
  "table-id": string;
  "file-id": string;
  "app-id": string;
  header: string[][];
  rows: any[][];
  name: string;
}

export interface SearchTableRequestTableId {
  "app-id": string;
  "table-id": string;
}

export interface SearchTableRequestFileId {
  "app-id": string;
  "file-id": string;
}

export type SearchTableRequest =
  | SearchTableRequestFileId
  | SearchTableRequestTableId;
